import './App.css';

import { useState, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DragProvider } from './components/DragContext';
import { openPlotWindow, closePlotWindow, updatePlot, isPlotWindowOpen } from './components/PlotWindow';

import { GameReducer } from './GameReducer';
import { actionTypes, defaultInitialState, makeDefaultPlanet } from './data';
import { getRandomPlanetName } from './utils/planetNames';

import { loadFromLocalStorage, saveToLocalStorage } from './helpers/storage';

import PlanetDisplay from './components/PlanetDisplay';
import Overview from './components/Overview';
import Timeline from './components/Timeline';
import SettingsModal from './components/SettingsModal';
import BurgerMenu from './components/BurgerMenu';
import PlanetList from './components/PlanetList';
import EmpireView from './components/EmpireView';

function App() {
  const [initialState, setInitialState] = useState(() =>{
      let state = loadFromLocalStorage('initialState', defaultInitialState)
      if(state.version === undefined || state.version < defaultInitialState.version)
      {
        state = defaultInitialState;
      }
      // Add planet time and index
      for(const planetId of Object.keys(state.planets))
      {
        state.planets[planetId].time = 0;
        state.planets[planetId].index = 0;
        state.planets[planetId].fleetActions = [];
      }
      // Try to fill in 'new'  ships
      for(const key of Object.keys(defaultInitialState.planets[1].ships))
      {
        for(const planetId of Object.keys(state.planets))
        {
          if(! (key in state.planets[planetId].ships))
          {
            console.log("Adding", key)
            state.planets[planetId].ships[key] = 0;
          }

        }
      }
      // Try to fill in 'new' defenses (whole object may be missing on old saves)
      for(const planetId of Object.keys(state.planets))
      {
        if(! state.planets[planetId].defenses)
        {
          state.planets[planetId].defenses = {};
        }
      }
      for(const key of Object.keys(defaultInitialState.planets[1].defenses))
      {
        for(const planetId of Object.keys(state.planets))
        {
          if(! (key in state.planets[planetId].defenses))
          {
            console.log("Adding", key)
            state.planets[planetId].defenses[key] = 0;
          }

        }
      }
      for(const key of Object.keys(defaultInitialState.research))
      {
        if(! (key in state.research))
        {
          console.log("Adding", key)
          state.research[key] = 0;
        }
      }
      // Fill in speed settings for saves predating user-editable speeds
      if (state.ressSpeed === undefined) state.ressSpeed = defaultInitialState.ressSpeed;
      if (state.buildSpeed === undefined) state.buildSpeed = defaultInitialState.buildSpeed;
      if (state.researchSpeed === undefined) state.researchSpeed = defaultInitialState.researchSpeed;
      // Assign random image names to planets that don't have one
      let stateUpdated = false;
      for(const planetId of Object.keys(state.planets))
      {
        if(!state.planets[planetId].imageName)
        {
          state.planets[planetId].imageName = getRandomPlanetName();
          stateUpdated = true;
        }
      }
      // Save updated state if we added image names
      if(stateUpdated)
      {
        saveToLocalStorage('initialState', state);
      }
      return state;
    }
  );
  const [buildOrder, setBuildOrder] = useState(() => {
    var _builderOrder = {};
    Object.keys(initialState.planets).forEach((planetId) => {
      _builderOrder[planetId] = [];
    })
    return _builderOrder;
  });
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  useEffect(() => {
    if (isSettingsOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => {
      // Clean up in case component is unmounted
      document.body.classList.remove('no-scroll');
    };
  }, [isSettingsOpen]);

  const [currentGameState, currentTimeLine, plotData] = GameReducer(initialState, buildOrder);
  
  // Update plot window when game state changes
  useEffect(() => {
    if (isPlotWindowOpen()) {
      updatePlot(currentGameState, currentTimeLine, plotData);
    }
  }, [currentGameState, currentTimeLine, plotData]);
  
  // Close plot window when component unmounts
  useEffect(() => {
    return () => {
      closePlotWindow();
    };
  }, []);

  // Function to download the current state as a JSON file
  const downloadStateAsJson = () => {
    const json = JSON.stringify(buildOrder, null, 2); // Pretty-print with indentation
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    // Create a temporary link and trigger the download
    const link = document.createElement("a");
    link.href = url;
    link.download = "buildOrder.json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Free up the URL object
  };
  
  // Function to download the current state with settings as a JSON file
  const downloadStateWithSettingsAsJson = () => {
    // Use the live initialState, not localStorage -- handleFileUploadWithSettings
    // deliberately updates initialState without persisting it (so an upload
    // doesn't clobber the user's own saved settings), so reading from
    // localStorage here would silently re-export stale, pre-upload settings
    // instead of whatever was actually just uploaded or edited this session.
    const stateWithSettings = {
      buildOrder: buildOrder,
      settings: initialState
    };
    const json = JSON.stringify(stateWithSettings, null, 2); // Pretty-print with indentation
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    // Create a temporary link and trigger the download
    const link = document.createElement("a");
    link.href = url;
    link.download = "buildOrderWithSettings.json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Free up the URL object
  };

  // Function to upload a JSON file and parse it as the new state
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const uploadedBuildOrder = JSON.parse(event.target.result);
          
          // Ensure all planets from initialState have entries in buildOrder
          const mergedBuildOrder = { ...uploadedBuildOrder };
          Object.keys(initialState.planets).forEach((planetId) => {
            if (!mergedBuildOrder[planetId]) {
              mergedBuildOrder[planetId] = [];
            }
          });
          
          setBuildOrder(mergedBuildOrder);
        } catch (error) {
          alert("Invalid JSON file");
        }
      };
      reader.readAsText(file);
    }
  };
  
  // Function to upload a JSON file with settings
  const handleFileUploadWithSettings = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const uploadedData = JSON.parse(event.target.result);
          
          if (uploadedData.buildOrder && uploadedData.settings) {
            // Set the build order
            const mergedBuildOrder = { ...uploadedData.buildOrder };
            Object.keys(uploadedData.settings.planets).forEach((planetId) => {
              if (!mergedBuildOrder[planetId]) {
                mergedBuildOrder[planetId] = [];
              }
            });
            setBuildOrder(mergedBuildOrder);
            
            // Set the settings temporarily (don't save to localStorage)
            const newSettings = uploadedData.settings;
            
            // Add planet time and index if they don't exist
            for(const planetId of Object.keys(newSettings.planets)) {
              newSettings.planets[planetId].time = 0;
              newSettings.planets[planetId].index = 0;
              if (!newSettings.planets[planetId].fleetActions) {
                newSettings.planets[planetId].fleetActions = [];
              }
            }
            
            // Set the settings without saving to localStorage
            setInitialState(newSettings);
          } else {
            alert("Invalid file format. Expected both buildOrder and settings.");
          }
        } catch (error) {
          alert("Invalid JSON file");
        }
      };
      reader.readAsText(file);
    }
  };

  // Function to update and save initial state to local storage
  const updateInitialState = (newState) => {
    setInitialState(newState);
    saveToLocalStorage('initialState', newState);
  };


  const handleMove = (planetId, index, direction, targetPlanetIdOrAction = null, targetIndex = null) => {
    setBuildOrder((prevTimeline) => {
      let newTimeLine = JSON.parse(JSON.stringify(prevTimeline));
      
      if (direction === 'add') {
        // Add a new action from planet display
        const newAction = targetPlanetIdOrAction;
        const targetPlanetId = planetId;
        if (newAction && targetPlanetId && newTimeLine[targetPlanetId]) {
          newTimeLine[targetPlanetId].splice(targetIndex, 0, newAction);
        }
        return newTimeLine;
      } else if (direction === 'move') {
        // Move between planets
        const targetPlanetId = targetPlanetIdOrAction;
        if (planetId && targetPlanetId && newTimeLine[planetId] && newTimeLine[targetPlanetId]) {
          const [movedAction] = newTimeLine[planetId].splice(index, 1);
          newTimeLine[targetPlanetId].splice(targetIndex, 0, movedAction);
        }
        return newTimeLine;
      } else {
        // Standard up/down movement
        const actualTargetIndex = targetIndex !== null ? 
          targetIndex : 
          direction === 'up' ? index - 1 : index + 1;
          
        if (planetId && newTimeLine[planetId] && actualTargetIndex >= 0 && actualTargetIndex <= newTimeLine[planetId].length) {
          // Swap elements
          const [movedAction] = newTimeLine[planetId].splice(index, 1);
          newTimeLine[planetId].splice(actualTargetIndex, 0, movedAction);
        }
        return newTimeLine;
      }
    });
  }

  const handleDelete = (planetId, index) => {
    setBuildOrder((prevTimeline) => {
      let newTimeLine = JSON.parse(JSON.stringify(prevTimeline));
      newTimeLine[planetId].splice(index, 1);
      return newTimeLine;
    });
  }
  
  const handleToggleDeactivate = (planetId, index) => {
    setBuildOrder((prevTimeline) => {
      let newTimeLine = JSON.parse(JSON.stringify(prevTimeline));
      // Toggle the deactivated property
      newTimeLine[planetId][index].deactivated = !newTimeLine[planetId][index].deactivated;
      return newTimeLine;
    });
  }
  
  // Track the currently dragged item and its preview state
  const [draggedItem, setDraggedItem] = useState(null);
  const [previewGameState, setPreviewGameState] = useState(null);
  const [previewTimeLine, setPreviewTimeLine] = useState(null);
  const [dragOverDropZone, setDragOverDropZone] = useState(false);

  // Update preview state when an item is being dragged
  const updatePreviewState = (draggedItem, targetPlanetId, targetIndex) => {
    if (!draggedItem) {
      setPreviewGameState(null);
      setPreviewTimeLine(null);
      setDragOverDropZone(false);
      return;
    }

    // Set flag that we're over a drop zone
    setDragOverDropZone(true);

    // Always create a fresh copy of the original buildOrder for each preview update
    // This ensures we can always return to the original state
    const tempBuildOrder = JSON.parse(JSON.stringify(buildOrder));
    
    if (draggedItem.type === 'TIMELINE_ITEM') {
      // Moving within timeline
      const sourcePlanetId = draggedItem.planetId;
      const sourceIndex = draggedItem.index;
      
      if (sourcePlanetId === targetPlanetId && sourceIndex === targetIndex) {
        // No change from original position - show original state
        setPreviewGameState(null);
        setPreviewTimeLine(null);
        return;
      }
      
      // Remove from source
      const [movedAction] = tempBuildOrder[sourcePlanetId].splice(sourceIndex, 1);
      
      // Add to target
      if (tempBuildOrder[targetPlanetId]) {
        // Insert at the target position
        tempBuildOrder[targetPlanetId].splice(targetIndex, 0, movedAction);
      }
    } else if (draggedItem.type === 'PLANET_ACTION') {
      // Adding from planet display
      const { actionType, payload } = draggedItem;
      
      if (tempBuildOrder[targetPlanetId]) {
        tempBuildOrder[targetPlanetId].splice(targetIndex, 0, {
          type: actionType,
          payload: payload
        });
      }
    }
    
    // Calculate the preview state based on the temporary build order
    const [previewState, previewTimeline] = GameReducer(initialState, tempBuildOrder);
    setPreviewGameState(previewState);
    setPreviewTimeLine(previewTimeline);
  };

  const handleUpgrade = (planetId, building) => {
    setBuildOrder((prevTimeline) => {
      let newTimeLine = JSON.parse(JSON.stringify(prevTimeline));
      newTimeLine[planetId] = [
        ... newTimeLine[planetId],
        {
          type: actionTypes.UPGRADE_BUILDING,
          payload: { planetId, building },
        }
      ];
      return newTimeLine;
  });
  };

  const handleTech = (planetId, tech) => {
    setBuildOrder((prevTimeline) => {
      let newTimeLine = JSON.parse(JSON.stringify(prevTimeline));
      newTimeLine[planetId] = [
        ... newTimeLine[planetId],
        {
          type: actionTypes.RESEARCH,
          payload: { planetId, tech },
        }
      ];
      return newTimeLine;
  });
  };


  

  const handleShip = (planetId, ship, count) => {
    setBuildOrder((prevTimeline) => {
      let newTimeLine = JSON.parse(JSON.stringify(prevTimeline));
      newTimeLine[planetId] = [
        ... newTimeLine[planetId],
        {
          type: actionTypes.BUILD_SHIP,
          payload: { planetId, ship, count },
        },
      ];
      return newTimeLine;
  });
  };

  const handleDefense = (planetId, defense, count) => {
    setBuildOrder((prevTimeline) => {
      let newTimeLine = JSON.parse(JSON.stringify(prevTimeline));
      newTimeLine[planetId] = [
        ... newTimeLine[planetId],
        {
          type: actionTypes.BUILD_DEFENSE,
          payload: { planetId, defense, count },
        },
      ];
      return newTimeLine;
  });
  };

  const changeSetProduction = (planetId, index, settings) => {
    setBuildOrder((prevTimeline) => {
      let newTimeLine = JSON.parse(JSON.stringify(prevTimeline));
      newTimeLine[planetId][index].payload.settings = settings;
      return newTimeLine;
    });
  }

  const addSetProduction = (planetId) => {
    setBuildOrder((prevTimeline) => {
      let newTimeLine = JSON.parse(JSON.stringify(prevTimeline));
      newTimeLine[planetId] = [
        ... newTimeLine[planetId],
        {
          type: actionTypes.SET_PROD,
          payload: { planetId, settings: {
            extra_wait: 0,
            metalMine: 1.0,
            crystalMine: 1.0,
            deuteriumSynthesizer: 1.0,
            awaitResearch: false,
            awaitBuilding: true,
            awaitHangar: false,
          }},
        },
      ]
      return newTimeLine;
    });
  }
  
  const changeTransportAction = (planetId, index, settings) => {
    setBuildOrder((prevTimeline) => {
      let newTimeLine = JSON.parse(JSON.stringify(prevTimeline));
      newTimeLine[planetId][index].payload.settings = settings;
      return newTimeLine;
    });
  }

  const addTransport = (planetId) => {
    setBuildOrder((prevTimeline) => {
      let newTimeLine = JSON.parse(JSON.stringify(prevTimeline));
      newTimeLine[planetId] = [
        ... newTimeLine[planetId],
        {
          type: actionTypes.TRANSPORT,
          payload: { planetId, settings: {
            metal: 1000,
            crystal: 0,
            deuterium: 0,
            flight_time: 0,
            target: 0,
          }},
        },
      ]
      return newTimeLine;
    });
  }

  const handleDeletePlanet = (planetId) => {
    var newState = {... initialState}
    if(newState.planets[planetId] !== undefined)
    {
      delete newState.planets[planetId]
      
      // Update buildOrder to remove the deleted planet
      const newBuildOrder = {...buildOrder};
      delete newBuildOrder[planetId];
      
      // Update active planet if needed
      if (activePlanetId === planetId) {
        const remainingPlanets = Object.keys(newState.planets);
        if (remainingPlanets.length > 0) {
          setActivePlanetId(remainingPlanets[0]);
        }
      }
      
      setInitialState(newState);
      setBuildOrder(newBuildOrder);
      saveToLocalStorage('initialState', newState);
    }
  }

  const handleAddPlanet = () => {
    var newState = {... initialState}
    const currKeys = Object.keys(newState.planets).map(Number);
    const newKey = Math.max(... currKeys) + 1
    setBuildOrder((prevTimeline) => {
      let newTimeLine = JSON.parse(JSON.stringify(prevTimeline));
      newTimeLine[newKey] = [];
      return newTimeLine;
    })
    
    // Get random planet image name
    const imageName = getRandomPlanetName();
    
    newState.planets[newKey] = makeDefaultPlanet("Kolonie", imageName);
    setInitialState(newState);
    saveToLocalStorage('initialState', newState);
  }

  const logoUrl = process.env.PUBLIC_URL + "/pr0game.png";

  // State for active planet
  const [activePlanetId, setActivePlanetId] = useState(Object.keys(initialState.planets)[0]);

  return (
    <DndProvider backend={HTML5Backend}>
      <DragProvider>
        <div className="App flex flex-col h-screen">
          {/* Settings Modal */}
          {isSettingsOpen && (
            <SettingsModal
              initialState={initialState}
              onSave={(newState) => {
                updateInitialState(newState);
                setSettingsOpen(false);
              }}
              onClose={() => setSettingsOpen(false)}
            />
          )}

          {/* Header with Logo and Burger Menu */}
          <header className="bg-gray-800 text-white p-2 shadow-md flex items-center justify-between">
            <div className="flex items-center">
              {/* Logo linking to game server */}
              <a href="https://pr0game.com" target="_blank" rel="noopener noreferrer" className="mr-4">
                <img src={logoUrl} alt="pr0game Logo" className="h-8" />
              </a>
              
              {/* App Title */}
              <h1 className="text-xl font-bold"><a href="https://pr0game.com" target="_blank" rel="noopener noreferrer">pr0game.com</a> Planning Tool</h1>
            </div>
            
            <div className="flex items-center">
              {/* Discord Button */}
              <a
                href="https://discord.gg/jhYYN3yuat"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg mr-4 flex items-center gap-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
                Join our Discord
              </a>
              
              {/* Burger Menu */}
              <BurgerMenu 
                onOpenSettings={() => setSettingsOpen(true)}
                onDownloadState={downloadStateAsJson}
                onDownloadWithSettings={downloadStateWithSettingsAsJson}
                onUploadState={handleFileUpload}
                onUploadWithSettings={handleFileUploadWithSettings}
                onShowPlot={() => openPlotWindow(currentGameState, currentTimeLine, plotData)}
              />
            </div>
          </header>
          
          {/* Main Content Area */}
          <main className="flex flex-1 overflow-hidden">
            {/* Planet List Sidebar */}
            <div className="w-20 flex-shrink-0">
              <PlanetList 
                planets={initialState.planets}
                activePlanetId={activePlanetId}
                onSelectPlanet={setActivePlanetId}
                onAddPlanet={handleAddPlanet}
              />
            </div>
            
            {/* Content Area with PlanetDisplay and Timeline or EmpireView */}
            {activePlanetId === 'empire' ? (
              <div className="flex-1 overflow-hidden">
                <EmpireView
                  changeTransportAction={changeTransportAction} 
                  changeSetProduction={changeSetProduction} 
                  totalTime={previewGameState ? previewGameState.time : currentGameState.time} 
                  planets={initialState.planets} 
                  timeline={previewTimeLine || currentTimeLine} 
                  handleDelete={handleDelete} 
                  handleMove={handleMove} 
                  handleToggleDeactivate={handleToggleDeactivate}
                  startTime={initialState.startTime}
                  setDraggedItem={setDraggedItem}
                  draggedItem={draggedItem}
                  updatePreviewState={updatePreviewState}
                />
              </div>
            ) : (
              <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                {/* Planet Display */}
                <div className="md:w-1/2 overflow-x-auto">
                  <PlanetDisplay
                    addTransport={addTransport}
                    addSetProduction={addSetProduction}
                    state={previewGameState || currentGameState}
                    handleUpgrade={handleUpgrade}
                    handleTech={handleTech}
                    handleShip={handleShip}
                    handleDefense={handleDefense}
                    handleAddPlanet={handleAddPlanet}
                    handleDeletePlanet={handleDeletePlanet}
                    setDraggedItem={setDraggedItem}
                    updatePreviewState={updatePreviewState}
                    activePlanetId={activePlanetId}
                  />
                </div>
                
                {/* Timeline */}
                <div className="md:w-1/2 overflow-x-auto border-t md:border-t-0 md:border-l border-gray-600">
                  <Timeline 
                    changeTransportAction={changeTransportAction} 
                    changeSetProduction={changeSetProduction} 
                    totalTime={previewGameState ? previewGameState.time : currentGameState.time} 
                    planets={initialState.planets} 
                    timeline={previewTimeLine || currentTimeLine} 
                    handleDelete={handleDelete} 
                    handleMove={handleMove} 
                    handleToggleDeactivate={handleToggleDeactivate}
                    startTime={initialState.startTime}
                    setDraggedItem={setDraggedItem}
                    draggedItem={draggedItem}
                    updatePreviewState={updatePreviewState}
                    activePlanetId={activePlanetId}
                  />
                </div>
              </div>
            )}
          </main>
        </div>
      </DragProvider>
    </DndProvider>
  );
}

export default App;
